import styled from "@emotion/styled"
import { PHeading, PInlineNotification, PText } from "@porsche-design-system/components-react"
import { colorContrastLow, spacingStaticSm } from "@porsche-design-system/components-react/emotion"
import { Fragment } from "react"
import { useController, useFormContext } from "react-hook-form"
import { defineMessages, FormattedMessage } from "react-intl"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"
import type { ListProps } from "src/components/list/List"
import { useConfig } from "src/hooks"
import { Checkbox, List, priceBreakdownMessages, Skeleton } from "../../../.."
import type { ListItemProps } from "../../../../list/ListItem"
import type { CalculatedPriceBreakdownView } from "../CalculatedPriceBreakdownView"

export interface ExtraCostsPriceSelectorFieldProps {
	className?: string
	name: string
}

const messages = defineMessages({
	categoryDescription: {
		defaultMessage: "You can manage the {categoryTitle} in the settings.",
		id: "extraCosts.vehicleDetailsPricingStageExtraCostCategoryDescription",
	},
	categoryTitle: {
		defaultMessage: "{categoryTitle} (gross)",
		id: "extraCostsCategoryTitle", // TODO: change id to match the pattern
	},
	categoryTitleHelperText: {
		defaultMessage: "No {categoryTitle} have been created. You can manage these in the settings",
		id: "extraCosts.vehicleDetailsPricingStageExtraCostCategoryHelperText",
	},
	priceShownToCustomerHint: {
		defaultMessage: "This price is visible in front of the customer.",
		id: "extraCosts.vehicleDetailsPricingStageExtraCostPriceShownToCustomerHint",
	},
	priceSourceHintMarketscan: {
		defaultMessage: "Dealer fees are managed in Marketscan and will be pulled from Marketscan directly.",
		id: "extraCosts.vehicleDetailsPricingStageExtraCostPriceSourceHintMarketscan",
	},
	totalCategoryPrice: {
		defaultMessage: "{categoryTitle} total",
		id: "extraCosts.vehicleDetailsPricingStageExtraCostCategoryTotal",
	},
	totalCategoryPricePrefix: {
		defaultMessage: "Total {categoryTitle}",
		id: "extraCosts.vehicleDetailsPricingStageExtraCostCategoryTotalPrefix",
	},
	totalPriceExclusive: {
		defaultMessage: "Listing Price (excl. taxes, excl. fees)",
		id: "extraCosts.vehicleDetailsPricingStageExtraCostTotalPriceExclusive",
	},
})

export const ExtraCostsPriceBreakdownPriceSelectorField = ({
	calculatedPriceBreakdownView,
	className,
	name,
	onExtraCostToggle,
}: ExtraCostsPriceSelectorFieldProps & {
	calculatedPriceBreakdownView: CalculatedPriceBreakdownView
	onExtraCostToggle: (isChecked: boolean) => void
}) => {
	const { vatDeductibleAvailable } = useConfig()
	const { control } = useFormContext()
	const {
		field: { onChange, value },
	} = useController({ control, defaultValue: [], name })

	if (calculatedPriceBreakdownView.state === "error") {
		return (
			<PInlineNotification state="error" dismissButton={false} actionIcon="exclamation">
				<FormattedMessage {...priceBreakdownMessages.genericError} />
			</PInlineNotification>
		)
	}

	return (
		<div className={className}>
			{calculatedPriceBreakdownView.view?.availableExtraCosts.categories.map((category, index) => (
				<Fragment key={`category-${index}`}>
					<HeadingWrapper>
						<PHeading size="sm">
							{vatDeductibleAvailable ? (
								<FormattedMessage
									{...messages.categoryTitle}
									values={{ categoryTitle: category.title }}
								/>
							) : (
								category.title
							)}
						</PHeading>
						<PText color="contrast-high">
							<FormattedMessage
								{...(category.extraCosts.length === 0
									? messages.categoryTitleHelperText
									: messages.categoryDescription)}
								values={{
									categoryTitle: category.title,
								}}
							/>
						</PText>
					</HeadingWrapper>
					{category.extraCosts.length > 0 && (
						<>
							<StyledListCategory>
								{category.extraCosts.map((ec) => (
									<List.Item
										key={ec.id}
										label={
											<Checkbox
												name={`extra-cost_${ec.id}`}
												checked={value.includes(ec.id)}
												onChange={(newValue) => {
													onChange(
														newValue
															? [...value, ec.id]
															: value.filter((v: string) => v !== ec.id),
													)
													onExtraCostToggle(newValue)
												}}
											>
												{ec.title}
											</Checkbox>
										}
										value={ec.grossPrice ?? <Skeleton.Text $width="80px" />}
									/>
								))}
							</StyledListCategory>

							{calculatedPriceBreakdownView.view?.priceBreakdown && (
								<StyledList>
									<SummaryListItem
										label={
											<PHeading size="sm">
												<FormattedMessage
													{...(vatDeductibleAvailable
														? messages.totalCategoryPrice
														: messages.totalCategoryPricePrefix)}
													values={{ categoryTitle: category.title }}
												/>
											</PHeading>
										}
										value={
											calculatedPriceBreakdownView.state === "loading" ||
											calculatedPriceBreakdownView.view.priceBreakdown?.categories[index]
												?.totalPrice === undefined ? (
												<Skeleton.Text $width="80px" />
											) : (
												calculatedPriceBreakdownView.view.priceBreakdown.categories[index]
													.totalPrice
											)
										}
									/>
								</StyledList>
							)}
						</>
					)}
				</Fragment>
			))}

			<StyledListSummary>
				<SummaryListItem
					label={
						<>
							<PHeading size="sm">
								{vatDeductibleAvailable ? (
									<FormattedMessage {...priceBreakdownMessages.totalPrice} />
								) : (
									<FormattedMessage {...messages.totalPriceExclusive} />
								)}
							</PHeading>

							<PText color="contrast-medium">
								{vatDeductibleAvailable ? (
									<FormattedMessage {...messages.priceShownToCustomerHint} />
								) : (
									<FormattedMessage {...messages.priceSourceHintMarketscan} />
								)}
							</PText>
						</>
					}
					value={
						calculatedPriceBreakdownView.state === "loading" ? (
							<Skeleton.Text size="lg" $width="160px" />
						) : (
							<PText size="lg" weight="semibold">
								{calculatedPriceBreakdownView.view?.priceBreakdown?.totalPrice ?? emptyPlaceholder}
							</PText>
						)
					}
				/>
			</StyledListSummary>
		</div>
	)
}

// dummy component wrapping is needed to avoid exceptions with vitest
const StyledList = styled((props: ListProps) => <List {...props} />)`
	li {
		align-items: center;

		&:first-of-type {
			margin: 0; /* since we're not using the header prop, negative margin needs to be overwritten */
		}
	}
`

const StyledListCategory = styled(StyledList)`
	border-top: 1px solid ${colorContrastLow};

	li {
		&:first-of-type {
			padding-top: ${spacingStaticSm};
		}
		&:last-of-type {
			padding-bottom: ${spacingStaticSm};
		}
		&:not(:last-of-type) {
			border: 0;
		}
	}
`

const StyledListSummary = styled(StyledList)``

const HeadingWrapper = styled.div`
	padding: ${spacingStaticSm} 0;
	&:first-of-type {
		padding-top: 0;
	}
	
	+ ${StyledListSummary} {
		border-top: 1px solid ${colorContrastLow};
	}
`

// dummy component wrapping is needed to avoid exceptions with vitest
const SummaryListItem = styled((props: ListItemProps) => <List.Item {...props} />)`
	padding: ${spacingStaticSm} 0;
`
